import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Check, X, ChevronDown, ChevronRight, Edit2, Trash2, GripVertical, Save } from 'lucide-react';

const ManageBands = () => {
  const [bands, setBands] = useState([]);
  const [expandedBands, setExpandedBands] = useState({});
  const [activeForm, setActiveForm] = useState(null);
  const [editingBand, setEditingBand] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    code: '',
    title: '',
    age_min_months: 0,
    age_max_months: 0,
    is_active: true,
  });
  const [milestoneImage, setMilestoneImage] = useState(null);
  const [goalImage, setGoalImage] = useState(null);
  const [milestoneImagePreview, setMilestoneImagePreview] = useState(null);
  const [goalImagePreview, setGoalImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchBands = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/children/global-bands/');
      setBands(response.data);
    } catch (error) {
      console.error('Error fetching bands:', error);
      showNotification('Error fetching bands', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBands();
  }, [fetchBands]);

  useEffect(() => {
    if (editingBand) {
      setFormData({
        code: editingBand.code,
        title: editingBand.title,
        age_min_months: editingBand.age_min_months,
        age_max_months: editingBand.age_max_months,
        is_active: editingBand.is_active,
      });
      setMilestoneImagePreview(editingBand.milestone_image);
      setGoalImagePreview(editingBand.goal_image);
      setMilestoneImage(null);
      setGoalImage(null);
    } else {
      setFormData({
        code: '',
        title: '',
        age_min_months: 0,
        age_max_months: 0,
        is_active: true,
      });
      setMilestoneImagePreview(null);
      setGoalImagePreview(null);
      setMilestoneImage(null);
      setGoalImage(null);
    }
    setErrors({});
  }, [editingBand]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleBandReorder = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setBands((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        return reordered.map((item, index) => ({ ...item, order: index }));
      });
      showNotification('Band order updated');
    }
  };

  const handleFormChange = (e) => {
    let { name, value, type, checked, files } = e.target;
    
    // Auto-format code/slug fields
    if (name === 'code') {
      value = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
    }

    if (type === 'file') {
      const file = files[0];
      if (name === 'milestone_image') {
        setMilestoneImage(file);
        if (file) {
          const reader = new FileReader();
          reader.onloadend = () => setMilestoneImagePreview(reader.result);
          reader.readAsDataURL(file);
        }
      } else if (name === 'goal_image') {
        setGoalImage(file);
        if (file) {
          const reader = new FileReader();
          reader.onloadend = () => setGoalImagePreview(reader.result);
          reader.readAsDataURL(file);
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (parseInt(formData.age_min_months) < 0) newErrors.age_min_months = 'Min age must be positive';
    if (parseInt(formData.age_max_months) < 0) newErrors.age_max_months = 'Max age must be positive';
    if (parseInt(formData.age_max_months) <= parseInt(formData.age_min_months)) {
      newErrors.age_max_months = 'Max age must be greater than min age';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const data = new FormData();
    for (const key in formData) {
      if (formData[key] !== null) {
        data.append(key, formData[key]);
      }
    }

    if (milestoneImage instanceof File) {
      data.append('milestone_image', milestoneImage);
    } else if (milestoneImage === null && editingBand?.milestone_image) {
      data.append('milestone_image', '');
    }

    if (goalImage instanceof File) {
      data.append('goal_image', goalImage);
    } else if (goalImage === null && editingBand?.goal_image) {
      data.append('goal_image', '');
    }

    try {
      if (editingBand) {
        const response = await api.patch(`/children/global-bands/${editingBand.id}/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setBands((bands) => bands.map((band) => (band.id === editingBand.id ? response.data : band)));
        showNotification('Band updated successfully');
      } else {
        const response = await api.post('/children/global-bands/', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setBands([...bands, response.data]);
        showNotification('Band created successfully');
      }
      setActiveForm(null);
      setEditingBand(null);
    } catch (error) {
      console.error('Error saving band:', error);
      if (error.response && error.response.data) {
        console.error('Backend validation errors:', error.response.data);
        const errorMsg = typeof error.response.data === 'object' 
          ? JSON.stringify(error.response.data) 
          : 'Error saving band';
        showNotification(errorMsg, 'error');
      } else {
        showNotification('Error saving band', 'error');
      }
    }
  };

  const deleteBand = async (bandId) => {
    if (!window.confirm('Are you sure you want to delete this band?')) return;

    try {
      await api.delete(`/children/global-bands/${bandId}/`);
      setBands((bands) => bands.filter((b) => b.id !== bandId));
      showNotification('Band deleted successfully');
    } catch (error) {
      console.error('Error deleting band:', error);
      showNotification('Error deleting band', 'error');
    }
  };

  const toggleActive = (bandId) => {
    setBands((bands) => bands.map((b) => (b.id === bandId ? { ...b, is_active: !b.is_active } : b)));
    showNotification('Band status toggled');
  };

  const filteredBands = bands.filter((band) => {
    const matchesSearch = band.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      band.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterActive === 'all' || (filterActive === 'active' ? band.is_active : !band.is_active);
    return matchesSearch && matchesFilter;
  });

  const SortableBandItem = ({ band }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: band.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const expanded = expandedBands[band.id];

    return (
      <div ref={setNodeRef} style={style} className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-100 hover:border-[#1e3a5f] transition-all">
        <div className="p-6">
          <div className="flex items-center gap-4">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#1e3a5f]">
              <GripVertical size={24} />
            </div>
            
            <button
              onClick={() => setExpandedBands({ ...expandedBands, [band.id]: !expanded })}
              className="text-[#1e3a5f] hover:bg-blue-50 p-2 rounded-lg transition-colors"
            >
              {expanded ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
            </button>

            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1 bg-[#1e3a5f] text-white text-xs font-bold rounded-full">{band.code}</span>
                <h3 className="text-xl font-bold text-[#1e3a5f]">{band.title}</h3>
                <span className="text-sm text-gray-500">({band.age_min_months}-{band.age_max_months} months)</span>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  band.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {band.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleActive(band.id)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-lg"
                title={band.is_active ? 'Deactivate' : 'Activate'}
              >
                {band.is_active ? '👁️' : '👁️‍🗨️'}
              </button>
              <button
                onClick={() => {
                  setEditingBand(band);
                  setActiveForm('band');
                }}
                className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
              >
                <Edit2 size={18} />
              </button>
              <button
                onClick={() => deleteBand(band.id)}
                className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          {expanded && (
            <div className="mt-6 pl-12 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {band.milestone_image && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Milestone Image</h4>
                    <img
                      src={band.milestone_image}
                      alt="Milestone"
                      className="w-full h-48 rounded-lg object-cover border-2 border-gray-200"
                    />
                  </div>
                )}
                {band.goal_image && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Goal Image</h4>
                    <img
                      src={band.goal_image}
                      alt="Goal"
                      className="w-full h-48 rounded-lg object-cover border-2 border-gray-200"
                    />
                  </div>
                )}
              </div>
              {!band.milestone_image && !band.goal_image && (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No images uploaded for this band</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl shadow-2xl animate-slide-in ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white font-semibold flex items-center gap-2`}>
          {notification.type === 'success' ? <Check size={20} /> : <X size={20} />}
          {notification.message}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] bg-clip-text text-transparent">
                Manage Bands
              </h1>
              <p className="text-gray-600 mt-2">Create, edit, and manage developmental bands</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setActiveForm('band');
                  setEditingBand(null);
                }}
                className="px-6 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Plus size={20} /> Add Band
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search bands..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'active', 'inactive'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setFilterActive(filter)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                    filterActive === filter
                      ? 'bg-[#1e3a5f] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {activeForm === 'band' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingBand ? 'Edit Band' : 'Add New Band'}
              </h2>
              <button
                onClick={() => {
                  setActiveForm(null);
                  setEditingBand(null);
                }}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    placeholder="e.g., Infancy"
                    value={formData.title}
                    onChange={handleFormChange}
                    className={`w-full px-3 py-2 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]`}
                  />
                  {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    placeholder="e.g., INF"
                    value={formData.code}
                    onChange={handleFormChange}
                    className={`w-full px-3 py-2 border ${errors.code ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]`}
                  />
                  {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Min Age (Months) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="age_min_months"
                    placeholder="0"
                    value={formData.age_min_months}
                    onChange={handleFormChange}
                    min="0"
                    className={`w-full px-3 py-2 border ${errors.age_min_months ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]`}
                  />
                  {errors.age_min_months && <p className="text-red-500 text-xs mt-1">{errors.age_min_months}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Max Age (Months) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="age_max_months"
                    placeholder="12"
                    value={formData.age_max_months}
                    onChange={handleFormChange}
                    min="0"
                    className={`w-full px-3 py-2 border ${errors.age_max_months ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]`}
                  />
                  {errors.age_max_months && <p className="text-red-500 text-xs mt-1">{errors.age_max_months}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Milestone Image
                  </label>
                  <input
                    type="file"
                    name="milestone_image"
                    onChange={handleFormChange}
                    accept="image/*"
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                  />
                  {milestoneImagePreview && (
                    <div className="mt-2">
                      <img src={milestoneImagePreview} alt="Milestone Preview" className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200" />
                      <button
                        type="button"
                        onClick={() => {
                          setMilestoneImage(null);
                          setMilestoneImagePreview(null);
                        }}
                        className="text-xs text-red-500 mt-1 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Goal Image
                  </label>
                  <input
                    type="file"
                    name="goal_image"
                    onChange={handleFormChange}
                    accept="image/*"
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                  />
                  {goalImagePreview && (
                    <div className="mt-2">
                      <img src={goalImagePreview} alt="Goal Preview" className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200" />
                      <button
                        type="button"
                        onClick={() => {
                          setGoalImage(null);
                          setGoalImagePreview(null);
                        }}
                        className="text-xs text-red-500 mt-1 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleFormChange}
                  className="w-4 h-4 text-[#1e3a5f] border-gray-300 rounded focus:ring-2 focus:ring-[#1e3a5f]"
                  id="is_active_inline"
                />
                <label htmlFor="is_active_inline" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Active Band
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setActiveForm(null);
                    setEditingBand(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors flex items-center gap-1"
                >
                  <X size={16} /> Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-lg font-semibold hover:shadow-lg transition-shadow flex items-center gap-1"
                >
                  <Save size={16} /> {editingBand ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-lg font-semibold text-gray-600">Loading bands...</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleBandReorder}>
            <SortableContext items={filteredBands.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {filteredBands.map((band) => (
                  <SortableBandItem key={band.id} band={band} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {!loading && filteredBands.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No Bands Found</h3>
            <p className="text-gray-500">Start by creating your first developmental band</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBands;